import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "golf-map-areas-container" }
const _hoisted_2 = { class: "sidebar" }
const _hoisted_3 = { class: "sidebar-header" }
const _hoisted_4 = { class: "areas-list" }
const _hoisted_5 = {
  key: 0,
  class: "no-areas"
}
const _hoisted_6 = ["onClick", "onMouseenter"]
const _hoisted_7 = { class: "area-info" }
const _hoisted_8 = { class: "area-name" }
const _hoisted_9 = { class: "area-actions" }
const _hoisted_10 = { class: "map-wrapper" }
const _hoisted_11 = {
  key: 0,
  class: "map-overlay-controls"
}
const _hoisted_12 = { class: "control-panel" }
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { key: 1 }
const _hoisted_15 = { class: "buttons" }
const _hoisted_16 = {
  key: 1,
  class: "cursor-coords"
}
const _hoisted_17 = { class: "dialog-footer" }

import { ref, onMounted, onUnmounted, reactive } from 'vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  getGolfAreas,
  createGolfArea,
  updateGolfArea,
  deleteGolfArea,
  type GolfArea,
  type GolfAreaPoint,
} from '@/api/golf-areas'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useGolfHeaderStore } from '@/store/golf-header'
import { useGolfI18n } from '@/composables/useGolfI18n'


export default /*@__PURE__*/_defineComponent({
  __name: 'GolfMapAreas',
  setup(__props) {

const headerStore = useGolfHeaderStore()
const { t } = useGolfI18n()

const mapContainer = ref<HTMLElement | null>(null)
let map: L.Map | null = null
let polygons: Record<string, L.Polygon> = {}
let tempPolyline: L.Polyline | null = null
let tempPolygon: L.Polygon | null = null
let editMarkers: L.Marker[] = []

// Data state
const allAreas = ref<GolfArea[]>([])
const areas = ref<GolfArea[]>([])
const loading = ref(false)
const selectedArea = ref<GolfArea | null>(null)
const hoveredAreaId = ref<string | null>(null)

// Interaction state
const isCreating = ref(false)
const isEditing = ref(false)
const newPoints = ref<GolfAreaPoint[]>([])
const cursorCoords = ref<{ lat: number; lng: number } | null>(null)

// Dialog state
const showDialog = ref(false)
const dialogMode = ref<'create' | 'edit'>('create')
const submitting = ref(false)
const formData = reactive({
  name: '',
  is_active: true,
})

onMounted(async () => {
  headerStore.setHeaderInfo(t('golf.fleet.resort_deployment_zone'), false)
  initMap()
  await loadAreas()
})

onUnmounted(() => {
  if (map) {
    map.remove()
  }
})

const initMap = () => {
  if (!mapContainer.value) return

  // Fix icons
  delete (L.Icon.Default.prototype as any)._getIconUrl
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  })

  map = L.map(mapContainer.value).setView([40.4093, 49.8671], 12)

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors',
  }).addTo(map)

  map.on('click', handleMapClick)

  map.on('mousemove', (e: L.LeafletMouseEvent) => {
    cursorCoords.value = { lat: e.latlng.lat, lng: e.latlng.lng }
  })
}

const loadAreas = async () => {
  loading.value = true
  try {
    const response = await getGolfAreas()
    allAreas.value = response.data.data.data || []
    areas.value = allAreas.value.filter((a) => a.type === 'parking')
    renderAreas()
  } catch (error) {
    console.error('Failed to load areas:', error)
    ElMessage.error(t('golf.map.failed_load_areas'))
  } finally {
    loading.value = false
  }
}

const renderAreas = () => {
  if (!map) return

  // Clear existing polygons
  Object.values(polygons).forEach((poly) => map?.removeLayer(poly))
  polygons = {}

  areas.value.forEach((area) => {
    if (!area.points || area.points.length === 0) return

    const latlngs = area.points.map((p) => [p.lat, p.lng] as L.LatLngExpression)
    const color = area.is_active ? '#2eb872' : '#F56C6C'

    const polygon = L.polygon(latlngs, {
      color: color,
      fillColor: color,
      fillOpacity: 0.3,
      weight: 2,
    }).addTo(map!)

    polygon.on('click', () => selectArea(area))
    polygons[area.id] = polygon
  })

  updatePolygonStyles()
}

const updatePolygonStyles = () => {
  Object.keys(polygons).forEach((id) => {
    const poly = polygons[id]
    const area = areas.value.find((a) => a.id === id)
    if (!area) return

    const isSelected = selectedArea.value?.id === id
    const isHovered = hoveredAreaId.value === id

    // If this area is being edited, keep it hidden so it doesn't overlap with edit markers
    if (isEditing.value && isSelected) {
      poly.setStyle({ opacity: 0, fillOpacity: 0 })
      return
    }

    const color = area.is_active ? '#2eb872' : '#F56C6C'
    const highlightColor = '#F56C6C'

    poly.setStyle({
      weight: isSelected || isHovered ? 4 : 2,
      color: isSelected || isHovered ? highlightColor : color,
      fillColor: color,
      fillOpacity: isHovered ? 0.45 : 0.3,
    })

    if (isSelected || isHovered) {
      poly.bringToFront()
    }
  })
}

const handleStatusChange = async (
  area: GolfArea,
  val: string | number | boolean
) => {
  const newValue = !!val
  try {
    await updateGolfArea(area.id, { is_active: newValue })
    ElMessage.success(t('golf.common.status_updated'))
    updatePolygonStyles()
  } catch (e) {
    console.error(e)
    area.is_active = !newValue
    ElMessage.error(t('golf.common.status_update_failed'))
  }
}

const selectArea = (area: GolfArea) => {
  if (isCreating.value) return
  // If editing another area, confirm switch? For now just block selection if editing
  if (isEditing.value && selectedArea.value?.id !== area.id) {
    ElMessage.warning(t('golf.map.finish_editing_first'))
    return
  }

  selectedArea.value = area
  updatePolygonStyles()

  // Smart pan: only move if the area is not currently visible
  const polygon = polygons[area.id]
  if (polygon && map) {
    const mapBounds = map.getBounds()
    const polyBounds = polygon.getBounds()

    // Check if polygon is substantially within view
    // We check if the center of the polygon is within the current map view
    if (!mapBounds.contains(polyBounds.getCenter())) {
      // If not visible, pan to it but keep current zoom
      map.panTo(polyBounds.getCenter())
    }
  }
}

const hoverArea = (area: GolfArea) => {
  if (isCreating.value || isEditing.value) return
  hoveredAreaId.value = area.id
  updatePolygonStyles()
}

const unhoverArea = () => {
  hoveredAreaId.value = null
  updatePolygonStyles()
}

const startCreating = () => {
  isCreating.value = true
  selectedArea.value = null
  updatePolygonStyles()
  newPoints.value = []

  if (map) {
    map.getContainer().style.cursor = 'crosshair'
  }
}

const handleMapClick = (e: L.LeafletMouseEvent) => {
  if (!isCreating.value && !isEditing.value) return

  const point = { lat: e.latlng.lat, lng: e.latlng.lng }

  if (isEditing.value) {
    // Find best index to insert
    const index = findBestInsertionIndex(point, newPoints.value)
    newPoints.value.splice(index, 0, point)
    renderTempPoly()
  } else {
    newPoints.value.push(point)
    renderTempPoly()
  }
}

const findBestInsertionIndex = (
  point: { lat: number; lng: number },
  points: GolfAreaPoint[]
) => {
  if (points.length < 2) return points.length

  let minDistance = Infinity
  let bestIndex = points.length

  for (let i = 0; i < points.length; i++) {
    const p1 = points[i]
    const p2 = points[(i + 1) % points.length] // Wrap around for closed polygon

    // Distance from point to line segment p1-p2
    const d = distanceFromPointToSegment(point, p1, p2)
    if (d < minDistance) {
      minDistance = d
      bestIndex = i + 1
    }
  }
  return bestIndex
}

const distanceFromPointToSegment = (
  p: { lat: number; lng: number },
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
) => {
  const x = p.lat
  const y = p.lng
  const x1 = a.lat
  const y1 = a.lng
  const x2 = b.lat
  const y2 = b.lng

  const A = x - x1
  const B = y - y1
  const C = x2 - x1
  const D = y2 - y1

  const dot = A * C + B * D
  const len_sq = C * C + D * D
  let param = -1
  if (len_sq !== 0)
    // in case of 0 length line
    param = dot / len_sq

  let xx, yy

  if (param < 0) {
    xx = x1
    yy = y1
  } else if (param > 1) {
    xx = x2
    yy = y2
  } else {
    xx = x1 + param * C
    yy = y1 + param * D
  }

  const dx = x - xx
  const dy = y - yy
  return Math.sqrt(dx * dx + dy * dy)
}

const updatePolyGeometry = () => {
  if (!map) return
  const latlngs = newPoints.value.map((p) => [p.lat, p.lng] as L.LatLngExpression)

  // Manage Polyline (for < 3 points)
  if (latlngs.length < 3) {
    // Remove polygon if it exists (we went from 3 to 2 points)
    if (tempPolygon) {
      map.removeLayer(tempPolygon)
      tempPolygon = null
    }

    if (tempPolyline) {
      tempPolyline.setLatLngs(latlngs)
    } else if (latlngs.length > 0) {
      tempPolyline = L.polyline(latlngs, { color: '#F56C6C', weight: 4 }).addTo(map)
      tempPolyline.on('click', (e: any) => {
        L.DomEvent.stopPropagation(e)
        handleMapClick(e)
      })
    }
  } else {
    // Manage Polygon (for >= 3 points, closed loop)

    // Remove polyline if it exists (we went from 2 to 3 points)
    if (tempPolyline) {
      map.removeLayer(tempPolyline)
      tempPolyline = null
    }

    if (tempPolygon) {
      tempPolygon.setLatLngs(latlngs)
    } else {
      // Create polygon with stroke enabled to show closed loop
      tempPolygon = L.polygon(latlngs, {
        color: '#F56C6C',
        weight: 4,
        fillOpacity: 0.2,
        stroke: true,
      }).addTo(map)

      tempPolygon.on('click', (e: any) => {
        L.DomEvent.stopPropagation(e)
        handleMapClick(e)
      })
    }
  }
}

const lastPointIcon = L.divIcon({
  className: 'last-point-marker',
  html: '<div style="background-color: #E6A23C; width: 14px; height: 14px; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.3);"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
})

const renderTempPoly = () => {
  if (!map) return

  // Remove existing markers for creation
  editMarkers.forEach((m) => map!.removeLayer(m))
  editMarkers = []

  // Draw markers for points
  newPoints.value.forEach((p, index) => {
    const isLast = index === newPoints.value.length - 1
    const marker = L.marker([p.lat, p.lng], {
      draggable: true,
      icon: isLast && newPoints.value.length > 1 ? lastPointIcon : new L.Icon.Default(),
    }).addTo(map!)

    marker.on('drag', (e: any) => {
      const newLatLng = e.target.getLatLng()
      newPoints.value[index] = { lat: newLatLng.lat, lng: newLatLng.lng }
      updatePolyGeometry() // Only update geometry, don't recreate markers
    })

    // Right click to delete point
    marker.on('contextmenu', () => {
      if (newPoints.value.length <= 3) {
        ElMessage.warning(t('golf.map.area_must_have_3_points'))
        return
      }
      newPoints.value.splice(index, 1)
      renderTempPoly() // Re-render to update indices
    })

    // Tooltip for help
    marker.bindTooltip(isLast ? 'Last Point' : 'Drag to move<br>Right-click to delete', {
      direction: 'top',
      offset: [0, -10],
      opacity: 0.8,
    })

    // Click first point to close
    if (isCreating.value && index === 0 && newPoints.value.length > 2) {
      marker.on('click', finishDrawing)
    }

    editMarkers.push(marker)
  })

  updatePolyGeometry()
}

const finishDrawing = () => {
  if (newPoints.value.length < 3) {
    ElMessage.warning(t('golf.map.area_must_have_3_points'))
    return
  }

  formData.name = ''
  formData.is_active = true
  dialogMode.value = 'create'
  showDialog.value = true
}

const startEditing = (area: GolfArea) => {
  selectArea(area)
  isEditing.value = true
  newPoints.value = [...area.points] // Clone points

  // Hide original polygon
  if (polygons[area.id]) {
    polygons[area.id].setStyle({ opacity: 0, fillOpacity: 0 })
  }

  renderTempPoly() // Use same logic as creation to show editable markers
}

const saveEdit = async () => {
  if (!selectedArea.value) return

  formData.name = selectedArea.value.name
  formData.is_active = selectedArea.value.is_active
  dialogMode.value = 'edit'
  showDialog.value = true
}

const cancelAction = () => {
  isCreating.value = false
  isEditing.value = false
  newPoints.value = []

  if (tempPolyline) map?.removeLayer(tempPolyline)
  if (tempPolygon) map?.removeLayer(tempPolygon)
  tempPolyline = null
  tempPolygon = null

  editMarkers.forEach((m) => map?.removeLayer(m))
  editMarkers = []

  if (selectedArea.value && polygons[selectedArea.value.id]) {
    // Restore opacity if it was hidden during editing
    polygons[selectedArea.value.id].setStyle({ opacity: 1, fillOpacity: 0.3 })
  }
  updatePolygonStyles()

  if (map) {
    map.getContainer().style.cursor = ''
  }
}

const handleDelete = async (area: GolfArea) => {
  try {
    await ElMessageBox.confirm('Are you sure you want to delete this area?', 'Warning', {
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      type: 'warning',
    })

    await deleteGolfArea(area.id)
    ElMessage.success(t('golf.map.area_deleted_success'))
    await loadAreas()
    selectedArea.value = null
  } catch (e) {
    if (e !== 'cancel') {
      console.error(e)
      ElMessage.error(t('golf.map.failed_delete_area'))
    }
  }
}

const submitForm = async () => {
  submitting.value = true
  try {
    const simplifiedPoints = simplifyPolygon(newPoints.value)

    if (dialogMode.value === 'create') {
      await createGolfArea({
        name: formData.name,
        type: 'parking',
        is_active: formData.is_active,
        points: simplifiedPoints,
      })
      ElMessage.success(t('golf.map.area_created_success'))
    } else {
      if (!selectedArea.value) return
      await updateGolfArea(selectedArea.value.id, {
        name: formData.name,
        is_active: formData.is_active,
        points: simplifiedPoints,
      })
      ElMessage.success(t('golf.map.area_updated_success'))
    }
    showDialog.value = false
    cancelAction()
    await loadAreas()
  } catch (error) {
    console.error(error)
    ElMessage.error(t('golf.map.failed_save_area'))
  } finally {
    submitting.value = false
  }
}

const areCollinear = (p1: GolfAreaPoint, p2: GolfAreaPoint, p3: GolfAreaPoint) => {
  const dist = distanceFromPointToLine(p2, p1, p3)

  return dist < 0.0001
}

const distanceFromPointToLine = (p: GolfAreaPoint, a: GolfAreaPoint, b: GolfAreaPoint) => {
  const x0 = p.lng,
    y0 = p.lat
  const x1 = a.lng,
    y1 = a.lat
  const x2 = b.lng,
    y2 = b.lat

  const numerator = Math.abs((x2 - x1) * (y1 - y0) - (x1 - x0) * (y2 - y1))
  const denominator = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))

  if (denominator === 0) return 0 // Points a and b are same

  return numerator / denominator
}

const simplifyPolygon = (points: GolfAreaPoint[]): GolfAreaPoint[] => {
  if (points.length <= 3) return points

  const res = [...points]
  let changeMade = true

  while (changeMade && res.length > 3) {
    changeMade = false
    for (let i = 0; i < res.length; i++) {
      const p1 = res[i]
      const p2 = res[(i + 1) % res.length]
      const p3 = res[(i + 2) % res.length]

      if (areCollinear(p1, p2, p3)) {
        // Remove the middle point p2
        res.splice((i + 1) % res.length, 1)
        changeMade = true
        break
      }
    }
  }
  return res
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_switch = _resolveComponent("el-switch")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h3", null, _toDisplayString(_ctx.$t('golf.map.parking_areas')), 1),
        _createVNode(_component_el_button, {
          type: "primary",
          size: "small",
          onClick: startCreating,
          disabled: isCreating.value || isEditing.value
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('golf.map.add_area')), 1)
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_4, [
        (areas.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_ctx.$t('golf.map.no_areas_found')), 1))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(areas.value, (area) => {
          return (_openBlock(), _createElementBlock("div", {
            key: area.id,
            class: _normalizeClass(["area-item", { 'is-active': selectedArea.value?.id === area.id }]),
            onClick: ($event: any) => (selectArea(area)),
            onMouseenter: ($event: any) => (hoverArea(area)),
            onMouseleave: unhoverArea
          }, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("span", _hoisted_8, _toDisplayString(area.name), 1),
              _createVNode(_component_el_switch, {
                modelValue: area.is_active,
                "onUpdate:modelValue": ($event: any) => ((area.is_active) = $event),
                size: "small",
                "active-text": _ctx.$t('golf.common.active'),
                "inactive-text": _ctx.$t('golf.fleet.status_inactive'),
                "inline-prompt": "",
                "active-color": "#fca82d",
                onChange: (val) => handleStatusChange(area, val),
                onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
              }, null, 8, ["modelValue", "onUpdate:modelValue", "active-text", "inactive-text", "onChange"])
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(_component_el_button, {
                type: "text",
                size: "small",
                icon: "el-icon-edit",
                onClick: _withModifiers(($event: any) => (startEditing(area)), ["stop"]),
                disabled: 
                isCreating.value || (isEditing.value && selectedArea.value?.id !== area.id)
              
              }, null, 8, ["onClick", "disabled"]),
              _createVNode(_component_el_button, {
                type: "text",
                size: "small",
                icon: "el-icon-delete",
                class: "delete-btn",
                onClick: _withModifiers(($event: any) => (handleDelete(area)), ["stop"]),
                disabled: isCreating.value || isEditing.value
              }, null, 8, ["onClick", "disabled"])
            ])
          ], 42, _hoisted_6))
        }), 128))
      ])), [
        [_directive_loading, loading.value]
      ])
    ]),
    _createElementVNode("div", _hoisted_10, [
      (isCreating.value || isEditing.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, [
              (isCreating.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(_ctx.$t('golf.map.drawing_instructions_create')), 1))
                : _createCommentVNode("", true),
              (isEditing.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(_ctx.$t('golf.map.area_instructions')), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_15, [
                (isCreating.value)
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 0,
                      type: "primary",
                      size: "small",
                      onClick: finishDrawing,
                      disabled: newPoints.value.length < 3
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('golf.map.finish_drawing')), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"]))
                  : _createCommentVNode("", true),
                (isEditing.value)
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 1,
                      type: "primary",
                      size: "small",
                      onClick: saveEdit
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('golf.common.save_changes')), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_el_button, {
                  size: "small",
                  onClick: cancelAction
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('golf.common.cancel')), 1)
                  ]),
                  _: 1
                })
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        ref_key: "mapContainer",
        ref: mapContainer,
        id: "golf-map-areas-map",
        class: "leaflet-map"
      }, null, 512),
      (cursorCoords.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(cursorCoords.value.lat.toFixed(6)) + ", " + _toDisplayString(cursorCoords.value.lng.toFixed(6)), 1))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_el_dialog, {
      modelValue: showDialog.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showDialog).value = $event)),
      title: 
        dialogMode.value === 'create'
          ? _ctx.$t('golf.map.create_new_area')
          : _ctx.$t('golf.map.edit_area_details')
      ,
      width: "400px",
      class: "golf-area-dialog"
    }, {
      footer: _withCtx(() => [
        _createElementVNode("span", _hoisted_17, [
          _createVNode(_component_el_button, {
            onClick: _cache[3] || (_cache[3] = ($event: any) => (showDialog.value = false))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('golf.common.cancel')), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: submitForm,
            loading: submitting.value
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('golf.common.confirm')), 1)
            ]),
            _: 1
          }, 8, ["loading"])
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_form, {
          model: formData,
          "label-width": "100px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: _ctx.$t('golf.common.name')
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: formData.name,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.name) = $event)),
                  placeholder: _ctx.$t('golf.map.area_name_placeholder')
                }, null, 8, ["modelValue", "placeholder"])
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_component_el_form_item, {
              label: _ctx.$t('golf.common.active')
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_switch, {
                  modelValue: formData.is_active,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.is_active) = $event)),
                  "active-color": "#fca82d"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }, 8, ["label"])
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})